'use client';

import { usePathname } from 'next/navigation';
import { ThemeProvider } from 'next-themes';
import type { ReactNode } from 'react';
import { env } from '@/lib/config/env';
import { useEarlyAccessGateStore } from '@/stores/early-access-gate';

/**
 * Wraps next-themes with a route-aware forced theme. Two dark-only surfaces are
 * forced regardless of the visitor's saved preference:
 *
 *  - Pre-launch, `/` is the marketing landing.
 *  - The early-access gate modal, on `/early-access` (where it always shows)
 *    and wherever else a locked visitor surfaces it. The gate publishes its
 *    visibility via `useEarlyAccessGateStore`; the `/early-access` route is also
 *    matched directly so that page is forced from first paint (no flash). The
 *    gate passes through to the landing and the self-gated `/rewards` without
 *    flagging the overlay, so those keep their own theme.
 *
 * Forcing at the provider keeps next-themes' pre-paint script authoritative, so
 * the route-matched surfaces never flash light before painting. `forcedTheme`
 * does NOT persist, so the user's saved light/dark choice still applies once
 * they're inside the app, and the app's theme toggle is untouched. Post-launch
 * the landing flag is off and `/` (the markets app) follows the user's theme.
 *
 * `localePrefix: 'never'` means routes carry no locale segment, so the bare
 * equality checks are sufficient.
 */
export function AppThemeProvider({ children }: { children: ReactNode }): ReactNode {
  const pathname = usePathname();
  const gateOverlayVisible = useEarlyAccessGateStore((s) => s.overlayVisible);

  const forceLandingDark = env.NEXT_PUBLIC_EARLY_ACCESS_ENABLED === 'true' && pathname === '/';
  const forceDark = forceLandingDark || pathname === '/early-access' || gateOverlayVisible;

  return (
    <ThemeProvider
      attribute="class"
      defaultTheme="dark"
      enableSystem
      forcedTheme={forceDark ? 'dark' : undefined}
    >
      {children}
    </ThemeProvider>
  );
}
